import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { Link, useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { allNotifications } from '../../slice/notificationSlice'

const STATUS_OPTIONS = ['in_review', 'resolved', 'rejected', 'pending']

const STATUS_BADGE = {
    in_review: { label: 'In Review', color: '#fd7e14', bg: '#fff3e0' },
    resolved: { label: 'Resolved', color: '#198754', bg: '#e8f5e9' },
    rejected: { label: 'Rejected', color: '#dc3545', bg: '#fdecea' },
    pending: { label: 'Pending', color: '#6c757d', bg: '#f1f3f5' },
}

const NotificationList = () => {
    const dispatch = useDispatch()
    const { notificationList } = useSelector(state => state.notification)
    const navigate = useNavigate()

    const {
        page,
        setPage,
        limit,
        setLimit,
        filter: statusFilter,
        setFilter: setStatusFilter,
        search: searchQuery,
        setSearch: setSearchQuery,
    } = useModuleSettings('notifications', { filter: 'all' })

    const [searchDebounce, setSearchDebounce] = useState(searchQuery)
    const isMounted = useRef(false)

    // Debounce search input
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            setSearchDebounce(searchQuery)
            return
        }
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allNotifications({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    const fetchList = () => {
        dispatch(allNotifications({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }

    const handleView = (notificationId) => {
        navigate('/notification/view', { state: { notificationId } })
    }

    // const handleChangeStatus = async (reportId, newStatus, adminNotes) => {
    //     try {
    //         const response = await dispatch(changeReportedBookingsStatus({
    //             id: reportId,
    //             status: newStatus,
    //             ...(adminNotes !== undefined && { admin_notes: adminNotes }),
    //         }))

    //         if (response?.payload?.code == 200) {
    //             Swal.fire({
    //                 toast: true,
    //                 position: 'top-end',
    //                 icon: 'success',
    //                 backdrop: false,
    //                 title: response?.payload?.message || 'Status updated successfully',
    //                 showConfirmButton: false,
    //                 timer: 1500,
    //             })
    //         }
    //         fetchList()
    //     } catch (error) {
    //         console.error('Error updating report status:', error)
    //     }
    // }

    const handleStatusSelect = async (row, newStatus) => {
        if (newStatus === row.status) return

        const badge = STATUS_BADGE[newStatus]

        // Prompt for optional admin notes when resolving or rejecting
        if (newStatus === 'resolved' || newStatus === 'rejected') {
            const { value: adminNotes, isConfirmed } = await Swal.fire({
                title: `Mark as ${badge.label}?`,
                input: 'textarea',
                inputLabel: 'Admin Notes (optional)',
                inputPlaceholder: 'Add a note about this decision...',
                inputAttributes: { rows: 3 },
                showCancelButton: true,
                confirmButtonColor: newStatus === 'resolved' ? '#198754' : '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: `Yes, mark as ${badge.label}`,
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            })

            if (!isConfirmed) return

            // await handleChangeStatus(row.id, newStatus, adminNotes || undefined)
        } else {
            // await handleChangeStatus(row.id, newStatus)
        }
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((notificationList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Title',
            selector: row => row.title,
            sortable: true,
            width: '450px',
        },
        {
            name: 'Message',
            selector: row => row.message,
            sortable: true,
            minWidth: '500px',
        },
        {
            name: 'Sent To',
            minWidth: '120px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#212529' }}>
                            {row.type?.toUpperCase()}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Send On',
            selector: row =>
                row.createdAt ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A') : 'NA',
            sortable: true,
            minWidth: '150px',
        },
        {
            name: 'Action',
            width: '80px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('notification', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: { style: { backgroundColor: '#ffffff', borderRadius: '8px' } },
        headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', minHeight: '48px' } },
        headCells: { style: { fontSize: '13px', fontWeight: '600', color: '#495057', paddingLeft: '12px', paddingRight: '12px' } },
        cells: { style: { fontSize: '13px', color: '#212529', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '12px', paddingRight: '12px' } },
        rows: {
            style: { minHeight: '60px', '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' } },
            stripedStyle: { backgroundColor: '#ffffff' },
        },
        pagination: { style: { borderTop: '1px solid #dee2e6', minHeight: '56px', fontSize: '13px' } },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Notification</h4>
                {hasPermission('notification', 'add') && (
                    <Link
                        to="/notification/send"
                        className="btn btn-success"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        + Send Notification
                    </Link>
                )}
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '16px' }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by title or message..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                                style={{ height: '40px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px' }}
                            >
                                <option value="all">All</option>
                                <option value="in_review">In Review</option>
                                <option value="resolved">Resolved</option>
                                <option value="rejected">Rejected</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={notificationList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={notificationList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={notificationList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => { setLimit(newLimit); setPage(1) }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={notificationList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-inbox" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No reports found matching your search' : 'No reported bookings found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Reported bookings will appear here'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}


export default NotificationList;